/**
 * Normalizes caseCounts (object keyed by case index, or array) into entries with an index key.
 *
 * @param {Record<string, object>|object[]|null|undefined} caseCounts
 * @returns {Array<{ key: string, entry: object }>}
 */
function getCaseCountEntries(caseCounts) {
    if (!caseCounts) {
        return [];
    }
    if (Array.isArray(caseCounts)) {
        return caseCounts.map((entry, idx) => ({
            key: String(idx + 1),
            entry: entry ?? {},
        }));
    }
    return Object.keys(caseCounts).map((key) => ({
        key: String(key),
        entry: caseCounts[key] ?? {},
    }));
}

/**
 * Builds display rows of case number + case weight for the Units per Case summary.
 *
 * @param {Record<string, { caseWeight?: string|number|null }>|Array<{ caseWeight?: string|number|null }>|null|undefined} caseCounts
 * @returns {Array<{ caseIdx: string, caseWeight: string|number|null|undefined }>}
 */
export function getCaseWeightSummaryRows(caseCounts) {
    return getCaseCountEntries(caseCounts).map(({ entry, key }) => ({
        caseIdx: key,
        caseWeight: entry.caseWeight,
    }));
}

/**
 * Resolves the case weight UOM from the first case that has one.
 *
 * @param {Record<string, { caseWeightUom?: string|null }>|Array<{ caseWeightUom?: string|null }>|null|undefined} caseCounts
 * @returns {string|null}
 */
export function getCaseWeightUom(caseCounts) {
    const entries = getCaseCountEntries(caseCounts);
    for (const { entry } of entries) {
        if (entry.caseWeightUom) {
            return entry.caseWeightUom;
        }
    }
    return null;
}

/**
 * Averages numeric case weights, rounded to 2 decimal places as a string.
 *
 * @param {Array<{ caseWeight?: string|number|null|undefined }>} rows
 * @returns {string|null}
 */
export function getAverageCaseWeight(rows) {
    if (!rows?.length) {
        return null;
    }

    const numericWeights = rows
        .map((row) => {
            if (row.caseWeight === '' || row.caseWeight === null || row.caseWeight === undefined) {
                return null;
            }
            const weight = Number(row.caseWeight);
            return Number.isFinite(weight) ? weight : null;
        })
        .filter((weight) => weight !== null);

    if (numericWeights.length === 0) {
        return null;
    }

    const sum = numericWeights.reduce((acc, weight) => acc + weight, 0);
    return (sum / numericWeights.length).toFixed(2);
}
